import useIsMobile from "@/hooks/useIsMobile";
import dynamic from "next/dynamic";

const AsiaCupPage = dynamic(() => import("@/screens/AsiaCupPage/AsiaCupPage"));
const PageLayout = dynamic(() => import("@/screens/Layout/PageLayout"));

export default function Category({
  categoryNewsListData = null,
  latestStoryData = null,
  latestVideoData = null,
  faqData = null,
  matchData = null,
  statsData = null,
}) {
  const isMobileDevice = useIsMobile();
  
  return (
    <>
      <PageLayout
        rightSideBarData={categoryNewsListData}
        latestStoryData={latestStoryData}
        latestVideoData={latestVideoData}
        isMobileDevice={isMobileDevice}
      >
        <AsiaCupPage
          categoryNewsListData={categoryNewsListData}
          isMobileDevice={isMobileDevice}
          faqData={faqData}
          matchData={matchData}
          currentTab={2}
          statsData={statsData}
        />
      </PageLayout>
    </>
  );
}

export const getServerSideProps = async (ctx) => {
  const { logger } = await import("@/utils/logging/log-util");
  try {
    const { getDataByUrl } = await import("@/handlers/commonApi");
    const { getLatestContentFeed } = await import("@/handlers/content");
    const { getFaqBySlug } = await import("@/handlers/faq");
    const { getMasterConfig } = await import("@/handlers/masterConfig");
    const { getRedisApiKey, fetchRedisWrapper } = await import(
      "@/handlers/redisKey"
    );
    const {
      getLiveMatches,
      getStatsPlayer,
      getRecentMatches,
      getUpcomingMatches,
    } = await import("@/handlers/ipl");

    const { parseMatchData } = await import("@/utils/ipl");
    const { setPageTTL } = await import("@/utils/setPageTTL");
    const { getUnixTimeDaysAgo } = await import("@/utils/dateTimeFormat");
    const unixtime = getUnixTimeDaysAgo(15);
    let masterConfigData = await getMasterConfig();

    //* get page from query param and subtract 1 from it , also error handle if not a number
    let page = ctx?.query?.page;
    if (isNaN(page)) {
      page = 0;
    }
    page = page ? parseInt(page) - 1 : 0;

    let categoryListRedisKey = getRedisApiKey({
      masterConfigData: masterConfigData,
      redisPrefix: "PREFIX_REDIS_ROS",
      params: {
        api: "getdatabyurl",
        url: "/sports",
        page: page,
        display: 6,
        type: "story",
      },
    });

    let faqRedisKey = getRedisApiKey({
      masterConfigData: masterConfigData,
      redisPrefix: "PREFIX_REDIS_ROS",
      params: { api: "staticpages", url: "asia-cup-2025-faqs" },
    });

    const latestStoryRedisKey = getRedisApiKey({
      masterConfigData: masterConfigData,
      redisPrefix: "PREFIX_REDIS_ROS",
      params: {
        api: "latestcontentfeed",
        page: 0,
        display: 5,
        type: "story",
        unixtime,
      },
    });
    const latestVideoRedisKey = getRedisApiKey({
      masterConfigData: masterConfigData,
      redisPrefix: "PREFIX_REDIS_ROS",
      params: {
        api: "latestcontentfeed",
        page: 0,
        display: 5,
        type: "video",
        unixtime,
      },
    });

    const getStats = async () => {
        try {
          const statsRes = await getStatsPlayer(13274, 0);
          if (statsRes && statsRes.stats?.length) {
            const stats = [
              "Highest Score",
              "Most Runs",
              "Most Wickets",
              "Best Economy Rates",
              "Best Batting Average",
              "Best Strike Rate",
              "Most Fifties",
              "Most Hundreds",
            ];
            const filteredStats = statsRes.stats
              .filter((stat) => stats.includes(stat.stat_name))
              .map((stat) => ({
                statName: stat.stat_name,
                statLeaderBoard: stat.leaderboard.slice(0,5),
              }));
            return filteredStats;
          }
          return null;
        } catch (err) {
          logger.error(err);
        }
      };
  
    const getMatches = async () => {
      try {
        let matchArr = [];

        // Fetch all data in parallel to reduce load time
        const [liveMatchData, recentMatchData, upcomingMatchData] =
          await Promise.all([
            getLiveMatches(13274),                         //  13274 series id for asia cup
            getRecentMatches(13274),
            getUpcomingMatches(13274),
          ]);

        // Process Live Matches First (Max 6)
        if (liveMatchData.length) {
          matchArr = liveMatchData.map(parseMatchData);
        }
        // Add Recent Match If Needed
        if (matchArr.length < 6 && recentMatchData?.data?.length) {
          if (upcomingMatchData?.data?.length >= 3) {
            matchArr.push(parseMatchData(recentMatchData?.data[0]));
          } else {
            for (let i = 0; i < 3; i++) {
              if (matchArr.length === 6) break;
              matchArr.push(parseMatchData(recentMatchData.data[i]));
            }
          }
        }

        // Add Upcoming Matches Until 6
        if (matchArr.length < 6 && upcomingMatchData?.data?.length) {
          const upcomingArr = upcomingMatchData?.data;
          for (let i = upcomingArr.length - 1; i >= 0; i--) {
            if (matchArr.length === 6) break;
            matchArr.push(parseMatchData(upcomingArr[i]));
          }
        }
        return matchArr;
      } catch (err) {
        logger.error(err);
      }
    };

    let [
      categoryNewsListData,
      latestStoryData,
      latestVideoData,
      faqData,
      matchData,
      statsData,
    ] = await Promise.all([
      fetchRedisWrapper(categoryListRedisKey, () =>
        getDataByUrl({
          url: "/sports",
          page: page,
          display: 6,
          type: "story",
        })
      ),
      fetchRedisWrapper(latestStoryRedisKey, () =>
        getLatestContentFeed({
          type: "story",
          page: 0,
          display: 5,
          unixtime,
        })
      ),
      fetchRedisWrapper(latestVideoRedisKey, () =>
        getLatestContentFeed({
          type: "video",
          page: 0,
          display: 5,
          unixtime,
        })
      ),
      fetchRedisWrapper(faqRedisKey, () =>
        getFaqBySlug({
          url: "asia-cup-2025-faqs",
        })
      ),
      getMatches(),
      getStats(),
    ]);

    setPageTTL(ctx, 120);
    return {
      props: {
        categoryNewsListData,
        latestStoryData,
        latestVideoData,
        faqData,
        matchData,
        statsData
      },
    };
  } catch (error) {
    logger.error(error);
    return {
      notFound: true,
    };
  }
};
